/** Provides a controlled hook for managing multiple task selections within a list. */
import { useRef, useCallback } from 'react';
import {
  selectSingleTaskId,
  toggleTaskIdInSelection,
  selectRangeTaskIds,
} from '@/features/tasks/hooks/list/use-list-selection.helpers';

/**
 * A controlled hook to manage task selection logic within a list.
 * It does not hold its own state for selected tasks.
 *
 * @param {object} props
 * @param {string[]} props.selectedTaskIds - The currently selected task IDs.
 * @param {(ids: string[]) => void} props.onSelectionChange - Callback to update the selected task IDs.
 * @returns {{
 *   taskListRef: import('react').RefObject<HTMLElement|null>,
 *   selectSingleTask: (id: string) => void,
 *   toggleTaskInSelection: (id: string) => void,
 *   selectRangeTasks: (ids: string[]) => void,
 *   deselectAllTasks: () => void,
 *   focusContainer: () => void,
 * }}
 */
const useTaskSelectionAndFocus = ({ selectedTaskIds, onSelectionChange }) => {
  const taskListRef = useRef(null);

  const focusContainer = useCallback(() => {
    taskListRef.current?.focus({ preventScroll: true });
  }, []);

  const deselectAllTasks = useCallback(() => {
    if (onSelectionChange) {
      onSelectionChange([]);
    }
  }, [onSelectionChange]);

  const selectSingleTask = useCallback(
    (taskId) => selectSingleTaskId(onSelectionChange, taskId),
    [onSelectionChange],
  );

  const toggleTaskInSelection = useCallback(
    (taskId) =>
      toggleTaskIdInSelection(onSelectionChange, selectedTaskIds, taskId),
    [selectedTaskIds, onSelectionChange],
  );

  const selectRangeTasks = useCallback(
    (ids) => selectRangeTaskIds(onSelectionChange, ids),
    [onSelectionChange],
  );

  return {
    taskListRef,
    selectSingleTask,
    toggleTaskInSelection,
    selectRangeTasks,
    deselectAllTasks,
    focusContainer,
  };
};

export default useTaskSelectionAndFocus;
